import React, { useContext } from "react";
import { Group, Text } from "@mantine/core";
import { Navigate } from "react-router-dom";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import { dateOnlyFormatYY } from "../../../utils/DateTime";
import { AuthContext } from "../../../providers/AuthProvider";
import ErrorPage from "../../../common/pages/ErrorPage";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import useEmployeeDataImportListApiService from "../services/api/EmployeeDataImportListApiService";
import EmployeeDataImportTableMenu from "../elements/EmployeeDataImportTableMenu";
import EmployeeDataImportListApiResponse from "../services/model/EmployeeDataImportListApiResponse";
import EmployeeImportStatusBadge from "../elements/EmployeeImportStatusBadge";
import EmployeeTableEmptyStateContainer from "./EmployeeTableEmptyStateContainer";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";

type EmployeeImportHistoryTableProps = {
  selection: EmployeeDataImportListApiResponse[];
  onSelection: React.Dispatch<
    React.SetStateAction<EmployeeDataImportListApiResponse[]>
  >;
};

const EmployeeImportHistoryTable: React.FC<EmployeeImportHistoryTableProps> = ({
  selection,
  onSelection,
}) => {
  const auth = useContext(AuthContext);
  const tableMaxHeight = ResponsiveHeight(
    "",
    "76vh",
    "76vh",
    "76vh",
    "67vh",
    "76vh"
  );

  const { isLoading, isError, error, data } =
    useEmployeeDataImportListApiService({
      employer_id: auth?.employer?.id ?? 0,
    });

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (!data) {
    return <EmployeeTableEmptyStateContainer />;
  }

  if (!data.length) {
    return <EmployeeTableEmptyStateContainer />;
  }

  return (
    <DirectoryTable
      tableMaxHeight={tableMaxHeight}
      data={data}
      selection={selection}
      onSelect={onSelection}
      headers={[
        "Uploaded by",
        "File name",
        "Date uploaded",
        "Total",
        "Updated",
        "New",
        "Deleted",
        "Skipped",
        "Status",
        "",
      ]}
      rows={data.map((it) => ({
        name: (
          <Group spacing="sm">
            <Text size="sm">
              {`${it.user_uploaded.first_name} ${it.user_uploaded.last_name[0]}.`}
            </Text>
          </Group>
        ),
        filename:
          it.file.length <= 30 ? it.file : `${it.file.substring(0, 30)}...`,
        uploadedDate: dateOnlyFormatYY(it.date_created),
        Total:
          (it.update_count || 0) +
          (it.create_count || 0) -
          (it.delete_count || 0),
        Uploaded: it.update_count || 0,
        New: it.create_count || 0,
        Deleted: it.delete_count || 0,
        Skipped: it.skip_count || 0,
        status: <EmployeeImportStatusBadge status={it.status} />,
        actions: <EmployeeDataImportTableMenu id={it.id} status={it.status} />,
      }))}
    />
  );
};

export default EmployeeImportHistoryTable;
